import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FaFingerprint, FaCreditCard, FaDownload, FaTrashAlt, FaCheckCircle, FaExclamationTriangle, FaSync, FaTimesCircle, FaStore } from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import { theme, t } from "../theme";
import { MAI_ATIVA } from '../config/features';
import { tokenizeCard } from "../services/pagarmeTokenize";
import { useConfirm } from "./ConfirmProvider";
import NotificacoesCard from "./NotificacoesCard";
import * as bio from "../platform/biometrics";
import FaturasHistorico from "./FaturasHistorico";
import { entregarArquivo } from '../platform/arquivos';
import { isNative } from '../platform/native';
import { podeColetarPagamento } from '../platform/cobranca';
import * as lojaBilling from '../platform/billing';

const TenantSettings = () => {
  const { tenant, refreshTenant, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [info, setInfo] = useState(null);
  const [nome, setNome] = useState("");
  const [nomeEdit, setNomeEdit] = useState(false);
  const [membros, setMembros] = useState([]);
  const [novoEmail, setNovoEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const [billing, setBilling] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [maiFab, setMaiFab] = useState(localStorage.getItem('mai_fab_disabled') !== 'true');
  // Biometria: so aparece no app, e so se o aparelho tiver digital/Face cadastrada.
  const [bioSuportada, setBioSuportada] = useState(false);
  const [bioAtiva, setBioAtiva] = useState(bio.ativa());
  useEffect(() => {
    if (!bio.disponivel()) return;
    bio.suportada().then(setBioSuportada).catch(() => {});
  }, []);

  const toggleBiometria = async () => {
    if (bioAtiva) { bio.definirAtiva(false); setBioAtiva(false); return; }
    // Exige uma autenticacao bem-sucedida ANTES de ligar: sem isso a usuaria
    // poderia se trancar fora do app com uma biometria que nao funciona.
    const r = await bio.autenticar({ motivo: 'Confirme sua identidade para ativar o bloqueio' });
    if (r.ok) { bio.definirAtiva(true); setBioAtiva(true); }
  };

  const toggleMaiFab = () => {
    const ativo = !maiFab;
    setMaiFab(ativo);
    localStorage.setItem('mai_fab_disabled', ativo ? 'false' : 'true');
    // O FAB reage na hora, sem recarregar a tela.
    window.dispatchEvent(new Event('mai-fab-pref-changed'));
  };

  useEffect(() => {
    loadInfo();
    loadMembros();
    loadBilling();
  }, []);

  const loadInfo = async () => {
    try {
      const res = await api.get("/tenant/info");
      setInfo(res.data);
      setNome(res.data.nome);
    } catch {
      setError("Erro ao carregar informações.");
    }
  };
  const loadMembros = async () => {
    try { const res = await api.get("/tenant/membros"); setMembros(res.data); } catch {}
  };
  const loadBilling = async () => {
    try { const res = await api.get("/billing/status"); setBilling(res.data); } catch {}
  };

  const handleSaveNome = async () => {
    setMsg(null); setError(null);
    const nomeTrim = (nome || '').trim();
    if (!nomeTrim) { setError("O nome não pode ficar vazio."); return; }
    try {
      const res = await api.put("/tenant/branding", {
        nome: nomeTrim,
        cor_primaria: info?.branding?.cor_primaria || '#C4786E',
        cor_secundaria: info?.branding?.cor_secundaria || '#E8B4A0',
      });
      setInfo(res.data);
      if (refreshTenant) refreshTenant({ ...tenant, ...res.data });
      setNomeEdit(false);
      setMsg("Nome atualizado!");
    } catch (err) { setError(err.response?.data?.erro || "Erro ao salvar."); }
  };

  const handleAddMembro = async () => {
    if (!novoEmail.trim()) return;
    setError(null);
    try {
      const res = await api.post("/tenant/membros", { email: novoEmail });
      setNovoEmail(""); setMsg("Membro adicionado!");
      setMembros(prev => [...prev, res.data]);
    } catch (err) { setError(err.response?.data?.erro || "Erro ao adicionar membro."); }
  };

  const handleRemoveMembro = async (id) => {
    setMembros(prev => prev.filter(m => m.id !== id));
    try { await api.delete(`/tenant/membros/${id}`); }
    catch (err) { setError(err.response?.data?.erro || "Erro ao remover."); loadMembros(); }
  };

  // Uma regra, um nome — ver `platform/cobranca`.
  const cobrancaDireta = podeColetarPagamento({
    nativo: isNative(),
    lojaDisponivel: lojaBilling.disponivel(),
  });
  const assinaturaDoPagarme = (billing?.billing_provider || 'pagarme') === 'pagarme';

  const handleCancelarAssinatura = async () => {
    const ok = await confirm(
      'Deseja cancelar sua assinatura? Você continuará com acesso até o fim do período atual.',
      { danger: true, confirmText: 'Cancelar assinatura' }
    );
    if (!ok) return;
    try {
      await api.delete('/billing/cancel');
      setMsg('Assinatura cancelada. Acesso mantido até o fim do período pago.');
      loadBilling();
    } catch (err) {
      setError(err.response?.data?.erro || 'Erro ao cancelar.');
    }
  };

  const handleReativar = () => navigate('/assinar');

  const handleExportData = async () => {
    setExportLoading(true); setError(null);
    try {
      const res = await api.get('/tenant/export-data', { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      await entregarArquivo(new Blob([res.data]), match ? match[1] : 'metria-export.zip');
      setMsg('Exportação concluída!');
    } catch (err) {
      setError(err.response?.data?.erro || 'Erro ao exportar dados.');
    } finally {
      setExportLoading(false);
    }
  };

  if (!info) return <div style={{ padding: 32 }}>Carregando...</div>;

  const limits = info.limites || {};
  const statusCfg = {
    active: { label: 'Ativa', cor: theme.colors.success, bg: theme.colors.successLight, icon: FaCheckCircle },
    past_due: { label: 'Pagamento pendente', cor: theme.colors.warning, bg: theme.colors.warningLight, icon: FaExclamationTriangle },
    canceled: { label: 'Cancelada', cor: theme.colors.danger, bg: theme.colors.dangerLight, icon: FaTimesCircle },
    pending_payment: { label: 'Aguardando pagamento', cor: theme.colors.warning, bg: theme.colors.warningLight, icon: FaExclamationTriangle },
    grandfathered: { label: 'Acesso liberado', cor: theme.colors.success, bg: theme.colors.successLight, icon: FaCheckCircle },
  }[billing?.subscription_status] || { label: '—', cor: theme.colors.textMuted, bg: theme.colors.borderLight, icon: FaCheckCircle };
  const StatusIcon = statusCfg.icon;

  const fmtData = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };
  const fmtDataCurta = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR');
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "24px 16px" }}>
      <h2 style={{ marginBottom: 4 }}>Configurações</h2>
      <p style={{ color: theme.colors.textMuted, fontSize: 14, marginBottom: 24 }}>
        {info.slug} &middot; Plano {info.plano_label}
      </p>

      {msg && <div style={styles.success}>{msg}</div>}
      {error && <div style={styles.error}>{error}</div>}

      {/* Assinatura */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>
          <FaCreditCard style={{ marginRight: 8, color: theme.colors.primary }} />
          Assinatura
        </h3>

        {billing ? (
          <>
            {/* Card de status */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '14px 16px', background: statusCfg.bg, borderRadius: 12, marginBottom: 16 }}>
              <StatusIcon style={{ color: statusCfg.cor, fontSize: 20, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: statusCfg.cor }}>{statusCfg.label}</div>
                <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                  Plano Completo · R$ {(billing.plan_valor || 39.90).toFixed(2).replace('.', ',')}/mês
                </div>
              </div>
            </div>

            {/* Detalhes */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
              <DetailBox label="Próxima cobrança" value={fmtDataCurta(billing.current_period_end)} />
              <DetailBox label="Último pagamento" value={fmtDataCurta(billing.last_payment_at)} />
              {billing.card_last4 && (
                <DetailBox label="Cartão" value={`${(billing.card_brand || '').toUpperCase() || 'Cartão'} •••• ${billing.card_last4}`} />
              )}
              {billing.canceled_at && (
                <DetailBox label="Cancelada em" value={fmtDataCurta(billing.canceled_at)} />
              )}
            </div>

            {/* Ações */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {billing.subscription_status === 'active' && (
                <>
                  {/* Cartão SÓ na web — guideline 3.1.1. Dentro do app qualquer
                      cobrança que não seja a da loja é motivo de rejeição, e esta
                      tela era uma delas: o formulário de cartão abria no iPhone. */}
                  {cobrancaDireta && (
                    <button onClick={() => setShowCardModal(true)} style={t.btnSecondary}>
                      <FaSync size={12} style={{ marginRight: 6 }} /> Atualizar cartão
                    </button>
                  )}
                  {/* Cancelar quem cobra pela loja não é com a gente: o botão
                      chamaria /billing/cancel, que mexe numa assinatura do
                      pagar.me que essa pessoa não tem. */}
                  {!billing.canceled_at && assinaturaDoPagarme && (
                    <button onClick={handleCancelarAssinatura} style={{ ...t.btnSecondary, color: theme.colors.danger, borderColor: theme.colors.danger + '40' }}>
                      Cancelar assinatura
                    </button>
                  )}
                  {!assinaturaDoPagarme && (
                    <div style={{ fontSize: 12, color: theme.colors.textSecondary, alignSelf: 'center' }}>
                      Assinatura gerenciada pela {billing.gerenciar_em || 'loja'} —
                      trocar o pagamento ou cancelar é pelos ajustes da sua conta lá.
                    </div>
                  )}
                </>
              )}
              {/* `trial` estava de fora, e era o estado mais comum: quem esta no
                  periodo gratuito abria esta tela para assinar e nao encontrava
                  botao nenhum. No app isso e pior — o banner de teste e o unico
                  caminho ate /assinar, e ele so aparece no topo de algumas telas. */}
              {['trial', 'past_due', 'pending_payment', 'canceled'].includes(billing.subscription_status) && (
                <button onClick={handleReativar} style={t.btnPrimary}>
                  {billing.subscription_status === 'past_due' ? 'Atualizar pagamento'
                    : billing.subscription_status === 'trial' ? 'Assinar agora'
                    : 'Reativar assinatura'}
                </button>
              )}
            </div>
          </>
        ) : (
          <p style={{ color: theme.colors.textSecondary, fontSize: 14 }}>Carregando dados da assinatura...</p>
        )}
      </section>

      {/* Confeitaria */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>
          <FaStore style={{ marginRight: 8, color: theme.colors.primary }} />
          Confeitaria
        </h3>
        <div style={styles.field}>
          <label style={styles.label}>Nome da confeitaria</label>
          {!nomeEdit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 600, color: theme.colors.text, padding: '10px 0' }}>
                {nome}
              </div>
              <button onClick={() => setNomeEdit(true)} style={{ ...t.btnSecondary, padding: '6px 14px', fontSize: 13 }}>
                Editar
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={nome} onChange={(e) => setNome(e.target.value)} style={{ ...styles.input, flex: 1 }} autoFocus />
              <button onClick={handleSaveNome} style={styles.btn}>Salvar</button>
              <button onClick={() => { setNome(info.nome); setNomeEdit(false); }} style={{ ...t.btnSecondary }}>Cancelar</button>
            </div>
          )}
        </div>
      </section>

      {/* Bloqueio por biometria — so no app nativo, so com biometria cadastrada */}
      {bioSuportada && (
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <FaFingerprint style={{ marginRight: 8, color: theme.colors.primary }} />
            Bloqueio do app
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1, paddingRight: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>Pedir digital ao abrir</div>
              <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                Você fica logada, mas ninguém abre o Metria no seu celular sem a sua digital
              </div>
            </div>
            <button
              onClick={toggleBiometria}
              role="switch"
              aria-checked={bioAtiva}
              aria-label="Pedir digital ao abrir"
              style={{
                flexShrink: 0, width: 46, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer',
                padding: 3, background: bioAtiva ? theme.colors.primary : theme.colors.border,
                transition: 'background 0.2s', display: 'flex',
                justifyContent: bioAtiva ? 'flex-end' : 'flex-start', alignItems: 'center',
              }}
            >
              <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
            </button>
          </div>
        </section>
      )}

      {/* Preferências — some junto com a Mai: sem ela o toggle nao controla nada */}
      {MAI_ATIVA && (
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>
          <img src="/mai-avatar.png" alt="" style={{ width: 22, height: 22, borderRadius: '50%', marginRight: 8, objectFit: 'cover', verticalAlign: 'middle' }} />
          Mai
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>Botão flutuante da Mai</div>
            <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
              Mostra o atalho da Mai no canto das telas. Você ainda pode falar com ela pelo menu.
            </div>
          </div>
          <button
            onClick={toggleMaiFab}
            role="switch"
            aria-checked={maiFab}
            aria-label="Botão flutuante da Mai"
            style={{
              flexShrink: 0, width: 46, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer',
              padding: 3, background: maiFab ? theme.colors.primary : theme.colors.border,
              transition: 'background 0.2s', display: 'flex',
              justifyContent: maiFab ? 'flex-end' : 'flex-start', alignItems: 'center',
            }}
          >
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
          </button>
        </div>
      </section>
      )}

      {/* Membros */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>
          Membros ({membros.length}/{limits.max_usuarios === -1 ? "ilimitado" : limits.max_usuarios})
        </h3>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {membros.map((m) => (
            <li key={m.id} style={styles.membroItem}>
              <span>
                {m.display_name || m.email}
                {m.is_owner && <span style={styles.ownerBadge}>owner</span>}
              </span>
              {!m.is_owner && (
                <button onClick={() => handleRemoveMembro(m.id)} style={styles.removeBtn}>Remover</button>
              )}
            </li>
          ))}
        </ul>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input value={novoEmail} onChange={(e) => setNovoEmail(e.target.value)} placeholder="email@exemplo.com" style={{ ...styles.input, flex: 1 }} />
          <button onClick={handleAddMembro} style={styles.btn}>Adicionar</button>
        </div>
      </section>

      {/* Histórico de faturas (billing) */}
      <FaturasHistorico />

      {/* Notificações push (PWA) */}
      <NotificacoesCard />

      {/* Privacidade e Dados (LGPD) — discreto */}
      <section style={{ ...styles.section, background: theme.colors.bgSection, border: `1px dashed ${theme.colors.border}` }}>
        <h3 style={{ ...styles.sectionTitle, fontSize: 13, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Privacidade e dados
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>Exportar meus dados</div>
            <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>Baixe um arquivo ZIP com todos os seus dados (LGPD)</div>
          </div>
          <button onClick={handleExportData} disabled={exportLoading} style={{ ...t.btnSecondary, whiteSpace: 'nowrap' }}>
            <FaDownload size={12} style={{ marginRight: 6 }} />
            {exportLoading ? 'Gerando...' : 'Baixar ZIP'}
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: `1px solid ${theme.colors.borderLight}` }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.danger }}>Excluir minha conta</div>
            <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>Remove todos os dados em até 15 dias conforme LGPD</div>
          </div>
          <button onClick={() => setShowDeleteModal(true)} style={{ ...t.btnSecondary, color: theme.colors.danger, borderColor: theme.colors.danger + '40', whiteSpace: 'nowrap' }}>
            <FaTrashAlt size={12} style={{ marginRight: 6 }} />
            Solicitar exclusão
          </button>
        </div>
      </section>

      {/* Dupla trava: o botao ja nao aparece no app, mas o modal e estado —
          bastaria alguem chamar setShowCardModal de outro lugar. */}
      {showCardModal && cobrancaDireta && (
        <UpdateCardModal
          onClose={() => setShowCardModal(false)}
          onSuccess={() => { setShowCardModal(false); setMsg('Cartão atualizado!'); loadBilling(); }}
        />
      )}

      {showDeleteModal && (
        <DeleteAccountModal
          tenantNome={tenant?.nome}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={async (motivo) => {
            try {
              await api.post('/tenant/request-deletion', { motivo, confirmacao: 'EXCLUIR' });
              setShowDeleteModal(false);
              setMsg('Solicitação registrada. Seus dados serão excluídos em até 15 dias. Você pode continuar usando até lá.');
              setTimeout(() => logout(), 5000);
            } catch (err) {
              setError(err.response?.data?.erro || 'Erro ao solicitar exclusão.');
            }
          }}
        />
      )}
    </div>
  );
};

const DetailBox = ({ label, value }) => (
  <div style={{ padding: '10px 14px', background: theme.colors.bgSection, borderRadius: 10, border: `1px solid ${theme.colors.borderLight}` }}>
    <div style={{ fontSize: 11, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>{value}</div>
  </div>
);

const UpdateCardModal = ({ onClose, onSuccess }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatCard = (v) => v.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim();
  const formatExp = (v) => { const d = v.replace(/\D/g, '').slice(0, 4); return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; };

  const handleSubmit = async (e) => {
    // `preventDefault` PRIMEIRO: sair antes dele deixaria o formulario fazer
    // submit nativo e recarregar a pagina.
    e.preventDefault();
    // Mesma trava do FaturasHistorico: o formulario nao renderiza no app, e o
    // envio tambem nao acontece. Ver `platform/cobranca`.
    if (!podeColetarPagamento({ nativo: isNative(), lojaDisponivel: lojaBilling.disponivel() })) return;
    setLoading(true); setError(null);
    try {
      const [mm, yy] = cardExp.split('/');
      const { token, last4, brand } = await tokenizeCard({
        number: cardNumber, holder_name: cardName, exp_month: mm, exp_year: yy, cvv: cardCvv,
      });
      await api.post('/billing/update-card', { card_token: token, card_last4: last4, card_brand: brand });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.erro || err.message || 'Erro ao atualizar cartão.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={t.overlay}>
      <div style={{ ...t.modal, maxWidth: 420 }}>
        <h3 style={{ margin: 0, marginBottom: 14 }}>Atualizar cartão</h3>
        {error && <div style={t.error}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 10 }}>
            <label style={t.label}>Número</label>
            <input value={cardNumber} onChange={e => setCardNumber(formatCard(e.target.value))} style={t.input} placeholder="0000 0000 0000 0000" inputMode="numeric" required />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={t.label}>Nome no cartão</label>
            <input value={cardName} onChange={e => setCardName(e.target.value.toUpperCase())} style={t.input} required />
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={t.label}>Validade</label>
              <input value={cardExp} onChange={e => setCardExp(formatExp(e.target.value))} style={t.input} placeholder="MM/AA" inputMode="numeric" required />
            </div>
            <div style={{ flex: 1 }}>
              <label style={t.label}>CVV</label>
              <input value={cardCvv} onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))} style={t.input} placeholder="123" inputMode="numeric" required />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={loading} style={{ ...t.btnPrimary, flex: 1, opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Atualizando...' : 'Atualizar'}
            </button>
            <button type="button" onClick={onClose} style={t.btnSecondary}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteAccountModal = ({ tenantNome, onClose, onConfirm }) => {
  const [confirma, setConfirma] = useState('');
  const [motivo, setMotivo] = useState('');
  const valid = confirma === 'EXCLUIR';

  return (
    <div style={t.overlay}>
      <div style={{ ...t.modal, maxWidth: 440 }}>
        <h3 style={{ margin: 0, marginBottom: 8, color: theme.colors.danger }}>Excluir conta</h3>
        <p style={{ fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.6, marginBottom: 16 }}>
          Isso solicitará a exclusão permanente de <strong>{tenantNome}</strong> e todos os dados relacionados:
          fichas técnicas, produtos, pedidos, clientes, estoque. Conforme LGPD, processamos em até 15 dias.
          Sua assinatura será cancelada automaticamente.
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={t.label}>Motivo (opcional)</label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} style={{ ...t.input, minHeight: 60, resize: 'vertical' }} placeholder="Nos conte por que está saindo..." />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={t.label}>Digite <strong>EXCLUIR</strong> para confirmar</label>
          <input value={confirma} onChange={e => setConfirma(e.target.value.toUpperCase())} style={t.input} placeholder="EXCLUIR" />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onConfirm(motivo)} disabled={!valid} style={{ ...t.btnPrimary, flex: 1, background: theme.colors.danger, opacity: valid ? 1 : 0.5 }}>
            Solicitar exclusão
          </button>
          <button onClick={onClose} style={t.btnSecondary}>Cancelar</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  section: { background: "#fff", borderRadius: 16, padding: "20px 24px", marginBottom: 20, boxShadow: "0 1px 3px rgba(61,44,44,0.06)", border: "1px solid #F7EFEC" },
  sectionTitle: { fontSize: 16, fontWeight: 700, marginBottom: 16, color: "#3D2C2C", display: 'flex', alignItems: 'center' },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#8C7A7A", marginBottom: 6 },
  input: { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #F0E6E2", fontSize: 14, boxSizing: "border-box", outline: "none", fontFamily: "'Inter', sans-serif" },
  btn: { padding: "10px 20px", borderRadius: 10, border: "none", background: "#C4786E", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  success: { background: "#EDF7F0", color: "#5DAE7B", padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 14 },
  error: { background: "#FBEAEA", color: "#D46B6B", padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 14 },
  membroItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 14 },
  ownerBadge: { background: "#f0e6ff", color: "#764ba2", fontSize: 11, padding: "2px 8px", borderRadius: 10, marginLeft: 8, fontWeight: 600 },
  removeBtn: { background: "none", border: "none", color: "#c0392b", fontSize: 13, cursor: "pointer" },
};

export default TenantSettings;
